import { HostBinding, Component, OnInit, Injector, ElementRef, ViewChild } from '@angular/core';
import { orderBy, isEmpty, find, forEach, isUndefined, startsWith } from 'lodash';
import * as bowser from 'bowser';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EventsComponent } from '../events/events.component';

@Component({
	selector: 'drop-down',
	// host: { '[class]': 'myCustomClass' },
	template: `
		<div
			class="dropdown-container"
			[ngClass]="{ 'full-autocomplete': fullAutocomplete, 'drp-disabled': control.disabled && !isMobile, error: error }"
		>
			<label *ngIf="control.attributes && control.attributes.label">{{ control.attributes.label }}</label>

			<label [attr.aria-label]="getAriaLabel()" (click)="onAccessibilityLabelClick($event)">
				<ng-select
					gtm-handler
					[attr.gtm]="gaLabel"
					[label]="gaLabel"
					events="click"
					handlerType="dataValue"
					action="DropDownChange"
					[value]="viewValue"
					[items]="control.options"
					[closeOnSelect]="true"
					[searchable]="true"
					[searchFn]="getSearchFn()"
					bindLabel="title"
					[disabled]="control.disabled"
					[clearable]="false"
					(change)="updateAnswer($event)"
					(click)="handleDropdownClick($event)"
					(keyup)="clearDropdown($event)"
					(blur)="handleDropdownBlur($event)"
					placeholder="{{ setPlaceHolder() }}"
					[(ngModel)]="viewValue"
				>
				</ng-select>
			</label>

			<div class="mobile-dropdown" [ngClass]="{ disabled: control.disabled, error: error }">
				<span></span>
				<select
					gtm-handler
					[attr.gtm]="gaLabel"
					[label]="gaLabel"
					events="click"
					[value]="viewValue"
					action="DropDownChange"
					handlerType="dataValue"
					[disabled]="control.disabled"
					(change)="updateAnswer($event.target)"
					[attr.aria-describedby]="control.name.split('.').join('_') + '_error'"
				>
					<option value="" disabled [attr.selected]="isDefaultSelected()">Select</option>
					<option
						[attr.selected]="setDefault(option.value)"
						*ngFor="let option of control.options"
						[value]="option.value"
					>
						{{ option.title }}
					</option>
				</select>
				<span></span>
			</div>
		</div>
	`,
	styleUrls: ['./drop-down.component.scss'],
})
export class DropDownComponent extends EventsComponent implements OnInit {
	@ViewChild(NgSelectComponent, { static: true })
	ngSelect: NgSelectComponent;
	viewValue: any;
	lastValue: any;
	@HostBinding('attr.name')
	name = this.control ? this.control.name : '';
	isOpen: boolean;
	isMobile: boolean;
	fullAutocomplete = false;
	title: string;
	lastSelectedChar: any;
	noResultsValue: boolean;
	filterCount: number = 0;
	isAtLeastOneMatch: boolean;

	constructor(injector: Injector, private el: ElementRef) {
		super(injector);
	}

	ngOnInit() {
		this.gaLabel = this.getGaLabel(this.control);
		if (this.value) {
			this.reportPrefill();
		}
		this.isMobile =
			bowser.getParser(window.navigator.userAgent).getPlatformType() === 'mobile' ||
			bowser.getParser(window.navigator.userAgent).getPlatformType() === 'tablet';
		this.el.nativeElement.classList.add(`col-${this.control.width}`);
		if (this.control.attributes) {
			this.el.nativeElement.classList.add(`${this.control.attributes.class}`);
			this.fullAutocomplete = this.control.attributes.fullAutocomplete;
			this.noResultsValue = this.control.attributes.noResultsValue;
		}
		this.isOpen = false;
		this.control.options = orderBy(this.control.options, 'sequence');
		this.title = '';
		let scrollContainer;
		let listContainer;
		let selectInput;
		const preventPageScroll = (event) => {
			if (event.deltaY < 0 && listContainer.scrollTop === 0) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();
			}

			const totalScroll = listContainer.scrollTop + listContainer.clientHeight;

			if (event.deltaY > 0 && totalScroll >= listContainer.scrollHeight) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();
			}
		};
		this.value = this.lastValue = this.setValue(this.value);
		if (!this.value) {
			this.value = this.lastValue = this.storeService.getValue(this.control.name, this.context);
		}
		this.viewValue = !isEmpty(this.value)
			? this.value
			: this.control.attributes && this.control.attributes.placeholder
			? this.control.attributes.placeholder
			: 'Select';

		this.observablesService.pageUpdatedFinished.subscribe(() => {
			if (this.control && this.control.options) {
				this.control.options = orderBy(this.control.options, 'sequence');
			}
		});
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();

		const elem = this.elementRef.nativeElement.querySelectorAll('input')[0];
		this.Renderer.setAttribute(elem, 'id', this.control.name.split('PolicyData.').join(''));
		this.Renderer.setAttribute(elem, 'aria-describedby', this.control.name + '_error');

		this.viewValue = !isEmpty(this.value)
			? this.value
			: this.control.attributes && this.control.attributes.placeholder
			? this.control.attributes.placeholder
			: 'Select';

		if (!this.fullAutocomplete) {
			elem.addEventListener('keyup', (event) => {
				// if backspace hit then reset list and show all options again.
				if (event.keyCode === 8 && isEmpty(elem.value)) {
					this.ngSelect.close();
					this.isOpen = false;
					this.ngSelect.open();
					this.isOpen = true;
				}
			});

			elem.addEventListener(
				'keypress',
				(e: Event) => {
					setTimeout(() => {
						if (this.lastSelectedChar === elem.value) {
							const options = this.elementRef.nativeElement.querySelectorAll('.ng-dropdown-panel-items .ng-option');
							let markedOption = '';
							forEach(options, (opt, index) => {
								if (opt.className.includes('ng-option-marked')) {
									markedOption = index;
								}
							});
							if (options.length === markedOption + 1) {
								this.Renderer.addClass(options[0], 'ng-option-marked');
							} else {
								this.Renderer.addClass(options[markedOption + 1], 'ng-option-marked');
							}
							this.Renderer.removeClass(options[markedOption], 'ng-option-marked');
						}
						this.lastSelectedChar = elem.value;

						if (isEmpty(this.lastSelectedChar)) {
							this.lastSelectedChar = this.lastSelectedChar.trim();
						}
						elem.value = '';
						this.Renderer.setValue(elem, '');
					});
				},
				0
			);
		}
	}

	setValue(value) {
		const option = find(this.control.options, { value: this.value });
		// this.title = option.title;
		if (option) {
			return option.title;
		} else {
			return this.value;
		}
	}

	updateAnswer(item) {
		this.splunkAnalyticsService.publish('DropdownChange', this.control.title, item.value);
		this.setAnswer(item.value);
		this.value = item.value;
		this.setValue(item.value);
		this.reportEditedPrefillValue();
	}

	setPlaceHolder() {
		this.viewValue = this.setValue(this.value);
		return !isEmpty(this.value)
			? this.value
			: this.control.attributes && this.control.attributes.placeholder
			? this.control.attributes.placeholder
			: 'Select';
	}

	setDefault(current) {
		if (current && this.value) {
			if (current.toLowerCase() === this.value.toLowerCase()) {
				return 'selected';
			}
		}
	}

	isDefaultSelected() {
		if (!this.value) return 'selected';
	}

	handleDropdownClick(e) {
		this.clearDropdown(e);
		if (this.isOpen) {
			this.ngSelect.close();
		}
		// this.isOpen = !this.isOpen;
	}

	handleDropdownBlur(e) {
		this.updateWithLastValue();
		this.isOpen = false;
	}

	clearDropdown(e) {
		const elem = this.elementRef.nativeElement.querySelector('.ng-value-label');
		if (elem != null && document.activeElement.attributes['aria-expanded']) {
			if (document.activeElement.attributes['aria-expanded'].value === 'false' && e.code !== 'Escape') {
				this.lastValue = elem.innerText ? elem.innerText : (this.lastValue = elem.innerText = this.viewValue);
				return;
			}
			elem.innerText = '';
		}
	}

	updateWithLastValue() {
		const elem = this.elementRef.nativeElement.querySelector('.ng-value-label');
		if (elem != null && !isUndefined(this.lastValue)) {
			elem.innerText = this.lastValue;
		}
		if (this.fullAutocomplete) {
			this.lastSelectedChar = '';
		}
	}

	resetSearch(event) {}

	getSearchFn() {
		if (this.noResultsValue) {
			this.filterCount = 0;
			this.isAtLeastOneMatch = false;
			return this.customNoItemsTextFilterFn.bind(this);
		}
		return this.fullAutocomplete ? null : this.filterByFirstChar;
	}

	filterByFirstChar(term: string, item: any) {
		if (isEmpty(term.trim())) {
			return item;
		}
		return startsWith(item.title.trim().toLowerCase(), term.trim().toLowerCase());
	}

	customNoItemsTextFilterFn(term: string, item: any): boolean {
		this.filterCount++;
		if (isEmpty(term.trim())) {
			return item;
		}
		const isMatch = item.title.trim().toLowerCase().includes(term.trim().toLowerCase());
		if (isMatch) {
			this.isAtLeastOneMatch = true;
			return isMatch;
		} else {
			if (this.filterCount === this.control.options.length && !this.isAtLeastOneMatch) {
				return true;
			}
		}
	}

	byTitle(option: any) {
		if (this.value) {
			return this.value.title === option.title;
		}
	}

	getAriaLabel(): string {
		let label = '';
		// if attributes.label is defined, should read another label instead
		if (this.control && (!this.control.attributes || !this.control.attributes.label)) {
			label = this.control.title;

			if (this.viewValue) {
				label += ` - ${this.viewValue} selected.`;
			}
		}
		return label;
	}

	onAccessibilityLabelClick(event: MouseEvent) {
		//preventDefault() is needed to prevent passing the click to ng-select, which results in closing the dropdown.
		event.preventDefault();
	}
}
